import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { unwrapApiResponse } from '@shared/api';
import { environment } from '../../environments/environment';

/**
 * Politica de contraseña, publicada por el servidor.
 *
 * ── POR QUE UN ENDPOINT Y NO UNA CONSTANTE ────────────────────────────────
 *
 * Aqui habia un `MIN_CONTRASENA = 12` tecleado a mano, copiado del
 * `appsettings` del backend. Antes de eso hubo un `8` inventado, y el sintoma
 * de esa version era el peor posible para el usuario: la pantalla acepta, el
 * servidor rechaza, y quien acaba de ser OBLIGADO a cambiar su contraseña
 * recibe un error despues de teclear algo que la pantalla le dijo que valia.
 *
 * Una constante duplicada no avisa cuando la otra copia cambia. El endpoint
 * sale de las MISMAS opciones que valida el servidor —comprobado del lado del
 * backend arrancando con `Contrasena__MinimoCaracteres=14`, y el endpoint
 * publico 14— asi que no es un literal disfrazado de API.
 *
 * ── ES ANONIMO, Y ESO IMPORTA ─────────────────────────────────────────────
 *
 * Lo natural habria sido colgarla de `sesion/actual`, y habria estado mal: la
 * pantalla de cambio FORZADO tiene sesion, pero la de restablecimiento llega
 * desde un enlace de correo y no tiene ninguna. Con `sesion/actual` se quitaria
 * la constante de una pantalla para dejarla tecleada en la otra.
 */
export interface PoliticaContrasena {
  readonly minimoCaracteres: number;
  readonly noPuedeSerIgualAlUsuario: boolean;
  /**
   * Reglas que el front **NO puede evaluar** (hoy una: distinta de la actual,
   * que exige el hash).
   *
   * ⚠️ No es informativo: es una advertencia. Estan aqui para poder ANUNCIARLAS
   * en la lista de requisitos sin fingir que se comprueban, y para que su 400 no
   * llegue como un fallo inesperado. Tratarlas como una regla mas que se valida
   * localmente seria mentir.
   */
  readonly soloElServidorPuede: readonly string[];
  /** Cuanto dura el enlace de restablecimiento. Para no teclear el 30 tampoco. */
  readonly minutosVigenciaEnlace: number;
}

/**
 * La que publica el servidor con su configuracion por defecto
 * (`Contrasena:MinimoCaracteres` 12, `MinutosVigenciaToken` 30, y la regla que
 * solo el puede evaluar en `ContrasenasService.Politica()`).
 *
 * Existe para `authSimulado`, que promete no llamar al backend en ningun
 * momento. Sin ella la pantalla pedia la politica igual: un 500 en consola en
 * cada carga, y el cambio forzado de `nuevo/nuevo` sin minimo que enseñar. Si
 * el servidor cambia sus valores, esta copia se queda vieja **solo en el
 * simulado**, que es justo donde no importa.
 */
const POLITICA_SIMULADA: PoliticaContrasena = {
  minimoCaracteres: 12,
  noPuedeSerIgualAlUsuario: true,
  soloElServidorPuede: ['La contraseña nueva debe ser distinta de la actual.'],
  minutosVigenciaEnlace: 30,
};

@Injectable({ providedIn: 'root' })
export class PoliticaContrasenaService {
  private readonly http = inject(HttpClient);

  /**
   * La politica, o `null` mientras no se haya podido cargar.
   *
   * `null` NO significa "sin restricciones": significa **"no la conocemos"**. La
   * pantalla degrada a solo `required` y deja que el servidor decida, que es lo
   * correcto — inventarse un minimo es como empezo este problema.
   */
  readonly politica = signal<PoliticaContrasena | null>(null);

  private peticion: Observable<PoliticaContrasena | null> | null = null;

  /** Se pide UNA vez y se comparte: no cambia entre pantallas de una sesion. */
  cargar(): Observable<PoliticaContrasena | null> {
    const origen: Observable<PoliticaContrasena> = environment.authSimulado
      ? of(POLITICA_SIMULADA)
      : this.http
          .get<unknown>(`${environment.apiUrl}/api/v1/sesion/contrasena/politica`)
          .pipe(unwrapApiResponse<PoliticaContrasena>());
    this.peticion ??= origen.pipe(
      tap((p) => this.politica.set(p)),
      // Que no se pueda leer la politica NO puede impedir cambiar la
      // contraseña: se sigue sin validacion local y manda el servidor.
      catchError(() => of(null)),
      map((p) => p),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.peticion;
  }
}
